import { MonitorDownIcon, XIcon } from 'lucide-react';
import { useEffect, useState, useSyncExternalStore } from 'react';

import { Button } from '@/components/ui/button.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip.js';
import { isRunningInTeams } from '@/lib/teams-env.js';
import { logger } from '@/logger.js';

import {
  installPromptState,
  runInstallPrompt,
  subscribeInstallPrompt,
} from './install-prompt.js';
import {
  DISMISS_STORAGE_KEY,
  dismissedRecently,
  installRoute,
  isStandaloneDisplay,
  manualInstallHint,
  type InstallRoute,
} from './install-promotion.js';

/** What the promotion says and does on this surface. */
interface Promotion {
  route: InstallRoute;
  body: string;
  /** The button text, or null when the surface only gets steps to follow. */
  action: string | null;
  /** The collapsed rail icon's name. */
  railLabel: string;
  run: () => void;
  dismiss: () => void;
}

/** The dismissal stored in this browser, or null when storage is unavailable. */
function storedDismissal(): string | null {
  try {
    return localStorage.getItem(DISMISS_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Remembers a dismissal; without storage it lasts for the page's life only. */
function storeDismissal(): void {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
  } catch {
    /* storage blocked — the in-memory flag still hides the card */
  }
}

/** Whether the page runs as the installed app right now. */
function runsStandalone(): boolean {
  return isStandaloneDisplay(
    (query) => window.matchMedia?.(query).matches ?? false,
    (navigator as Navigator & { standalone?: boolean }).standalone,
  );
}

/** Opens the agent in the person's browser from a Teams tab, where installing is possible. */
function openInBrowser(): void {
  window.open(
    `${window.location.origin}/home`,
    '_blank',
    'noopener,noreferrer',
  );
}

/** The copy and the action for a route, or null when there are no steps to give. */
function promotionFor(
  route: InstallRoute,
):
  | (Pick<Promotion, 'body' | 'action' | 'railLabel'> & { run: () => void })
  | null {
  if (route === 'prompt')
    return {
      body: 'Its own window, one click from the taskbar.',
      action: 'Install',
      railLabel: 'Install CT Agent',
      run: () => void runInstallPrompt(),
    };
  if (route === 'open-in-browser')
    return {
      body: 'Open it in your browser, then install it from there.',
      action: 'Open in browser',
      railLabel: 'Open in browser to install',
      run: openInBrowser,
    };
  const hint = manualInstallHint(navigator.userAgent, navigator.maxTouchPoints);
  if (!hint) return null;
  return {
    body: hint,
    action: null,
    railLabel: 'Install CT Agent',
    run: () => undefined,
  };
}

/**
 * The route this surface offers, or null inside the installed app, after an install and within
 * seven days of a dismissal; with the way to record a dismissal. The seven days are checked on
 * every render, so a page left open past them shows the card again.
 */
function useRoute(): [InstallRoute | null, () => void] {
  const { prompt, installed } = useSyncExternalStore(
    subscribeInstallPrompt,
    installPromptState,
  );
  const [dismissedAt, setDismissedAt] = useState(storedDismissal);
  const [inTeams] = useState(isRunningInTeams);
  const markDismissed = () => setDismissedAt(String(Date.now()));
  if (installed || dismissedRecently(dismissedAt, Date.now()))
    return [null, markDismissed];
  const route = installRoute({
    standalone: runsStandalone(),
    inTeams,
    promptReady: prompt !== null,
    nativePromptSupported: 'BeforeInstallPromptEvent' in window,
  });
  return [route, markDismissed];
}

/**
 * The install promotion for this surface, or null while it should stay hidden, including a browser
 * the promotion has no steps for. A shown promotion logs one impression.
 * shortcut: the impression, click, dismiss and choice events go to the console logger only; send
 * them to a server once the web client has an analytics sink.
 */
function useInstallPromotion(): Promotion | null {
  const [route, markDismissed] = useRoute();
  const copy = route ? promotionFor(route) : null;
  const shownRoute = copy ? route : null;
  useEffect(() => {
    if (shownRoute)
      logger.info('install_promotion', {
        action: 'impression',
        route: shownRoute,
      });
  }, [shownRoute]);
  if (!route || !copy) return null;
  return {
    ...copy,
    route,
    run: () => {
      logger.info('install_promotion', { action: 'click', route });
      copy.run();
    },
    dismiss: () => {
      logger.info('install_promotion', { action: 'dismiss', route });
      storeDismissal();
      markDismissed();
    },
  };
}

/**
 * Promotes installing CT Agent as an app (MEDA-1187), pinned under the sidebar's scrolling area. On
 * the collapsed rail it shrinks to an icon that runs the same action; dismissing needs the card, and
 * a surface that only gets steps shows nothing there.
 */
export function InstallAppCard({ collapsed }: { collapsed: boolean }) {
  const promotion = useInstallPromotion();
  if (!promotion) return null;
  if (collapsed)
    return promotion.action ? <InstallRailIcon promotion={promotion} /> : null;
  return (
    <div className="shrink-0 border-t border-border px-3 py-3">
      <div className="relative rounded-xl border border-border bg-card p-3 shadow-elev-1">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Dismiss"
          className="absolute right-1.5 top-1.5 text-muted-foreground"
          onClick={promotion.dismiss}
        >
          <XIcon className="size-4" />
        </Button>
        <div className="flex items-start gap-2.5 pr-6">
          <MonitorDownIcon className="mt-0.5 size-4 shrink-0 text-primary-text" />
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-snug">
              Install CT Agent
            </p>
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
              {promotion.body}
            </p>
          </div>
        </div>
        {promotion.action && (
          <Button size="sm" className="mt-3 w-full" onClick={promotion.run}>
            {promotion.action}
          </Button>
        )}
      </div>
    </div>
  );
}

/** The promotion on the collapsed rail: the action as an icon, named by its tooltip. */
function InstallRailIcon({ promotion }: { promotion: Promotion }) {
  return (
    <div className="flex shrink-0 justify-center border-t border-border py-3">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={promotion.railLabel}
            className="text-primary-text"
            onClick={promotion.run}
          >
            <MonitorDownIcon className="size-[length:var(--sidebar-nav-icon)]" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">{promotion.railLabel}</TooltipContent>
      </Tooltip>
    </div>
  );
}
